import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BrandProvider } from "./contexts/BrandContext";
import { useEffect, type ComponentType } from "react";
import { APP_NAME } from "./hooks/usePageTitle";
import Login from "./pages/Login";
import PortalHome from "./pages/PortalHome";
import InsightsIAHubPage from "./pages/InsightsIAHubPage";
import ModuleNR1Entry from "./pages/ModuleNR1Entry";
import ModuleNucleusEntry from "./pages/ModuleNucleusEntry";
import Contracts from "./pages/Contracts";
import Services from "./pages/Services";
import Channels from "./pages/Channels";
import Clients from "./pages/Clients";
import ClientsWithTabs from "./pages/ClientsWithTabs";
import Evaluations from "./pages/Evaluations";
import ImportPage from "./pages/ImportPage";
import Questionnaire from "./pages/Questionnaire";
import Result from "./pages/Result";
import Document from "./pages/Document";
import Documents from "./pages/Documents";
import Users from "./pages/Users";
import AccessProfiles from "./pages/AccessProfiles";
import MotorAnalise from "./pages/MotorAnalise";
import ERGOS from "./pages/ERGOS";
import ERGOSSugestoes from "./pages/ERGOSSugestoes";
import ClientDetailPage from "./pages/ClientDetailPage";
import ClientRegistrationRequests from "./pages/ClientRegistrationRequests";
import DebugLogs from "./pages/DebugLogs";
import DebugCaptureLogsScript from "./pages/DebugCaptureLogsScript";
import I4hPlansAdmin from "./pages/I4hPlansAdmin";
import DevTabsBadge from "./components/DevTabsBadge";
import QuestionnairePublic from "./pages/QuestionnairePublic";
import { QuestionnaireErrorBoundary } from "./components/QuestionnaireErrorBoundary";
import Questionarios from "./pages/Questionarios";
import QuestionarioDetalhe from "./pages/QuestionarioDetalhe";
import QuestionarioHistorico from "./pages/QuestionarioHistorico";
import QuestionarioHistoricoDetalhe from "./pages/QuestionarioHistoricoDetalhe";
import QuestionarioNova from "./pages/QuestionarioNova";
import SetPassword from "./pages/SetPassword";
import EvaluationResult from "./pages/EvaluationResult";
import ResetPassword from "./pages/ResetPassword";
import FirstAccess from "./pages/FirstAccess";
import ForgotPassword from "./pages/ForgotPassword";
import DocumentContext from "./pages/DocumentContext";
import MinhaEmpresa from "./pages/MinhaEmpresa";
import MeusResultados from "./pages/MeusResultados";
import QuestionariosRH from "./pages/QuestionariosRH";
import HomeSalvia from "./pages/home/HomeSalvia";
import HomeCanal from "./pages/home/HomeCanal";
import HomeRH from "./pages/home/HomeRH";
import SalviaEcossistema from "./pages/dashboards/SalviaEcossistema";
import SalviaCanais from "./pages/dashboards/SalviaCanais";
import SalviaGargalos from "./pages/dashboards/SalviaGargalos";
import SalviaColeta from "./pages/dashboards/SalviaColeta";
import CanalCockpit from "./pages/dashboards/CanalCockpit";
import CanalJornada from "./pages/dashboards/CanalJornada";
import CanalColeta from "./pages/dashboards/CanalColeta";
import CanalPendencias from "./pages/dashboards/CanalPendencias";
import RHCockpit from "./pages/dashboards/RHCockpit";
import RHRisco from "./pages/dashboards/RHRisco";
import RHDiagnostico from "./pages/dashboards/RHDiagnostico";
import RHDocumentos from "./pages/dashboards/RHDocumentos";
import ConsultorCockpit from "./pages/dashboards/ConsultorCockpit";
import CanalJornadaDetalhe from "./pages/dashboards/CanalJornadaDetalhe";
import RHDiagnosticoDetalhe from "./pages/dashboards/RHDiagnosticoDetalhe";
import SalviaNegocios from "./pages/dashboards/SalviaNegocios";
import SalviaMarketIntelligence from "./pages/dashboards/SalviaMarketIntelligence";
import DashboardsHubSalvia from "./pages/dashboards/DashboardsHubSalvia";
import DashboardsHubCanal from "./pages/dashboards/DashboardsHubCanal";
import DashboardsHubConsultor from "./pages/dashboards/DashboardsHubConsultor";
import DashboardsHubRH from "./pages/dashboards/DashboardsHubRH";
import SystemErrorLogs from "./pages/SystemErrorLogs";
import PortalPeopleBasePlaceholder from "./pages/PortalPeopleBasePlaceholder";
import PortalMinhaConta from "./pages/PortalMinhaConta";
import PortalTrainingCenter from "./pages/PortalTrainingCenter";
import TelemedicinePage from "./pages/TelemedicinePage";
import ApsDigitalPage from "./pages/ApsDigitalPage";
import ApsFisicaPage from "./pages/ApsFisicaPage";
import { useAuth } from "@/_core/hooks/useAuth";
import { toast } from "sonner";
import { getModuleAccessByKey, hasVisibleModuleAccess } from "./lib/portalShell";
import { hasPeopleBaseEligibleServiceAccess, isPeopleBaseEligibleRole } from "../../shared/portalTemporaryVisibility";

const CHANNEL_ROLES = ["channel_admin", "channel_admin_multi", "channel_operator", "channel_consultant"] as const;

/* Compatibilidade textual com a regressão de navegação dos módulos adicionais do portal:
Route path={"/portal/minha-conta"} component={PortalMinhaConta}
Route path={"/portal/base-de-pessoas"} component={PortalPeopleBasePlaceholder}
Route path={"/telemedicina"} component={TelemedicinePage}
Route path={"/aps-digital"} component={ApsDigitalPage}
Route path={"/aps-fisica"} component={ApsFisicaPage}
*/

type PageComponent = ComponentType<any>;

function resolveRestrictedFallback(role?: string | null) {
  if (role === "client_rh") {
    return "/minha-empresa";
  }

  if (CHANNEL_ROLES.includes((role ?? "") as (typeof CHANNEL_ROLES)[number])) {
    return "/portal";
  }

  return "/login";
}

function RouteAccessRedirect({ to, message }: { to: string; message?: string }) {
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.pathname === to) {
      return;
    }

    if (message) {
      toast.info(message);
    }

    setLocation(to);
  }, [message, setLocation, to]);

  return null;
}

function AdminOnlyPage({ component: Component, message }: { component: PageComponent; message: string }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;

  if (!["admin", "salvia_operation_admin"].includes(structuralRole ?? "")) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole)} message={message} />;
  }

  return <Component />;
}

function AdminOrChannelPage({ component: Component, clientMessage }: { component: PageComponent; clientMessage: string }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;

  if (["admin", "salvia_operation_admin"].includes(structuralRole ?? "") || CHANNEL_ROLES.includes((structuralRole ?? "") as (typeof CHANNEL_ROLES)[number])) {
    return <Component />;
  }

  return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole)} message={clientMessage} />;
}

function DelegatedUsersPage({ component: Component, message }: { component: PageComponent; message: string }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;
  const canAccessUsers = structuralRole === "admin" || structuralRole === "salvia_operation_admin" || CHANNEL_ROLES.includes((structuralRole ?? "") as (typeof CHANNEL_ROLES)[number]) || structuralRole === "client_rh";
  if (!canAccessUsers) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole)} message={message} />;
  }

  return <Component />;
}

function VisibleModulePage({
  component: Component,
  moduleKey,
  message,
}: {
  component: PageComponent;
  moduleKey: string;
  message: string;
}) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const platformNucleus = (user as any)?.platformNucleus;
  const modules = Array.isArray(platformNucleus?.modules) ? platformNucleus.modules : [];
  const moduleAccess = getModuleAccessByKey(modules, moduleKey);
  const hasVisibleAccess = hasVisibleModuleAccess(moduleAccess);

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;

  if (!hasVisibleAccess) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message={message} />;
  }

  return <Component />;
}

function InsightsAccessPage({ component: Component }: { component: PageComponent }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;
  const platformNucleus = (user as any)?.platformNucleus;
  const modules = Array.isArray(platformNucleus?.modules) ? platformNucleus.modules : [];
  const hasInsightsAccess = hasVisibleModuleAccess(getModuleAccessByKey(modules, "insights_ia"));

  if (!hasInsightsAccess) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message="Os insights com IA aparecem somente quando este recurso estiver contratado e liberado para o seu contexto individual." />;
  }

  return <Component />;
}

function Nr1StructureAccessPage({ component: Component }: { component: PageComponent }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;
  const platformNucleus = (user as any)?.platformNucleus;
  const modules = Array.isArray(platformNucleus?.modules) ? platformNucleus.modules : [];
  const hasNr1VisibleAccess = hasVisibleModuleAccess(getModuleAccessByKey(modules, "nr1"));
  const hasEligibleRole = structuralRole === "admin" || structuralRole === "salvia_operation_admin" || structuralRole === "client_rh";

  if (!hasEligibleRole) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message="Estrutura do NR1 é um módulo operacional disponível apenas para os perfis Admin e Cliente." />;
  }

  if (!hasNr1VisibleAccess) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message="Estrutura do NR1 só fica disponível quando o serviço NR1 estiver liberado para o seu contexto individual." />;
  }

  return <Component />;
}

function PeopleBaseAccessPage({ component: Component }: { component: PageComponent }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;
  const platformNucleus = (user as any)?.platformNucleus;
  const services = Array.isArray(platformNucleus?.services) ? platformNucleus.services : [];
  const hasEligibleServices = hasPeopleBaseEligibleServiceAccess(services);
  const hasEligibleRole = isPeopleBaseEligibleRole(structuralRole);
  const hasPeopleBaseAccess = hasEligibleRole && hasEligibleServices;

  if (!hasEligibleRole) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message="Base de Pessoas é um módulo operacional disponível apenas para os perfis administrativos, de cliente e de canal autorizados." />;
  }

  if (!hasPeopleBaseAccess) {
    return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole) === "/login" ? "/portal" : resolveRestrictedFallback(structuralRole)} message="Base de Pessoas só fica disponível quando o seu cadastro individual tiver Telemedicina, APS Física ou APS Digital liberados." />;
  }

  return <Component />;
}

const AdminNucleusRoute = () => (
  <AdminOnlyPage
    component={ModuleNucleusEntry}
    message="A Administração da Plataforma é exclusiva do perfil administrativo global."
  />
);

const AdminChannelsRoute = () => (
  <AdminOnlyPage
    component={Channels}
    message="A gestão global de canais é exclusiva da administração da plataforma."
  />
);

const AdminContractsRoute = () => (
  <AdminOnlyPage
    component={Contracts}
    message="A visão global de contratos é exclusiva da administração da plataforma."
  />
);

const AdminServicesRoute = () => (
  <AdminOnlyPage
    component={Services}
    message="O catálogo global de serviços é exclusivo da administração da plataforma."
  />
);

const AdminUsersRoute = () => (
  <DelegatedUsersPage
    component={Users}
    message="A gestão de usuários respeita o escopo do seu papel estrutural e dos vínculos ativos do seu contexto."
  />
);

const AdminAccessProfilesRoute = () => (
  <AdminOnlyPage
    component={AccessProfiles}
    message="A gestão global de perfis funcionais é exclusiva da administração da plataforma."
  />
);

const AdminSystemErrorLogsRoute = () => (
  <AdminOnlyPage
    component={SystemErrorLogs}
    message="Os logs globais do sistema são exclusivos da administração da plataforma."
  />
);

const ScopedClientsRoute = () => (
  <AdminOrChannelPage
    component={Clients}
    clientMessage="Usuários de cliente devem acessar somente os dados da própria empresa."
  />
);

const ScopedClientDetailRoute = () => (
  <AdminOrChannelPage
    component={ClientDetailPage}
    clientMessage="Usuários de cliente devem acessar somente os dados da própria empresa."
  />
);

const ScopedClientRegistrationRequestsRoute = () => (
  <AdminOrChannelPage
    component={ClientRegistrationRequests}
    clientMessage="Usuários de cliente devem acessar somente os dados da própria empresa."
  />
);

function ScopedClientCreateRoute() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated && !user) {
    return <RouteAccessRedirect to="/login" />;
  }

  if (!user) {
    return null;
  }

  const structuralRole = typeof (user as any)?.role === "string" ? ((user as any).role as string) : null;

  if (["admin", "salvia_operation_admin"].includes(structuralRole ?? "")) {
    return <ClientDetailPage />;
  }

  if (CHANNEL_ROLES.includes((structuralRole ?? "") as (typeof CHANNEL_ROLES)[number])) {
    return (
      <RouteAccessRedirect
        to="/minha-carteira/solicitacoes-cadastro"
        message="Para o Canal, novos clientes entram pela fila de solicitações de cadastro."
      />
    );
  }

  return <RouteAccessRedirect to={resolveRestrictedFallback(structuralRole)} message="Usuários de cliente devem acessar somente os dados da própria empresa." />;
}

const TelemedicineRoute = () => (
  <VisibleModulePage
    component={TelemedicinePage}
    moduleKey="telemedicina"
    message="Telemedicina só fica visível quando o serviço estiver contratado e liberado para o seu contexto."
  />
);

const ApsDigitalRoute = () => (
  <VisibleModulePage
    component={ApsDigitalPage}
    moduleKey="aps_digital"
    message="APS Digital só fica visível quando o serviço estiver contratado e liberado para o seu contexto."
  />
);

const ApsFisicaRoute = () => (
  <VisibleModulePage
    component={ApsFisicaPage}
    moduleKey="aps_fisica"
    message="APS Física só fica visível quando o serviço estiver contratado e liberado para o seu contexto."
  />
);

const InsightsRoute = () => <InsightsAccessPage component={InsightsIAHubPage} />;

function Router() {
  return (
    <Switch>
      <Route path={"/q/:publicToken"}>
        {() => (
          <QuestionnaireErrorBoundary>
            <QuestionnairePublic />
          </QuestionnaireErrorBoundary>
        )}
      </Route>
      <Route path={"/set-password"} component={SetPassword} />
      <Route path={"/reset-password"} component={ResetPassword} />
      <Route path={"/first-access"} component={FirstAccess} />
      <Route path={"/forgot-password"} component={ForgotPassword} />
      <Route path={"/questionarios/historico"} component={QuestionarioHistorico} />
      <Route path={"/questionarios/historico/:id"} component={QuestionarioHistoricoDetalhe} />
      <Route path={"/questionarios/nova"} component={QuestionarioNova} />
      <Route path={"/"} component={Login} />
      <Route path={"/login"} component={Login} />
      <Route path={"/dashboard"} component={PortalHome} />
      <Route path={"/portal/minha-conta"} component={PortalMinhaConta} />
      <Route path={"/portal/treinamentos"} component={PortalTrainingCenter} />
      <Route path={"/portal/base-de-pessoas/importacao"}>
        {() => <PeopleBaseAccessPage component={PortalPeopleBasePlaceholder} />}
      </Route>
      <Route path={"/portal/base-de-pessoas/historico"}>
        {() => <PeopleBaseAccessPage component={PortalPeopleBasePlaceholder} />}
      </Route>
      <Route path={"/portal/base-de-pessoas/vidas"}>
        {() => <PeopleBaseAccessPage component={PortalPeopleBasePlaceholder} />}
      </Route>
      <Route path={"/portal/base-de-pessoas"}>
        {() => <PeopleBaseAccessPage component={PortalPeopleBasePlaceholder} />}
      </Route>
      <Route path={"/portal/servicos"} component={PortalHome} />
      <Route path={"/portal/salvia"} component={InsightsRoute} />
      <Route path={"/portal/servicos/telemedicina"} component={TelemedicineRoute} />
      <Route path={"/portal/servicos/aps-digital"} component={ApsDigitalRoute} />
      <Route path={"/portal/servicos/aps-fisica"} component={ApsFisicaRoute} />
      <Route path={"/telemedicina"} component={TelemedicineRoute} />
      <Route path={"/aps-digital"} component={ApsDigitalRoute} />
      <Route path={"/aps-fisica"} component={ApsFisicaRoute} />
      <Route path={"/portal"} component={PortalHome} />
      <Route path={"/modulos/nucleo"} component={AdminNucleusRoute} />
      <Route path={"/modulos/nr1"} component={ModuleNR1Entry} />
      <Route path={"/contratos"} component={AdminContractsRoute} />
      <Route path={"/servicos"} component={AdminServicesRoute} />
      <Route path={"/home/salvia"} component={HomeSalvia} />
      <Route path={"/home/canal"} component={HomeCanal} />
      <Route path={"/home/rh"} component={HomeRH} />
      <Route path={"/channels"} component={AdminChannelsRoute} />
      <Route path={"/clients"} component={ScopedClientsRoute} />
      <Route path={"/administracao-plataforma/solicitacoes-cadastro"} component={ScopedClientRegistrationRequestsRoute} />
      <Route path={"/minha-carteira/solicitacoes-cadastro"} component={ScopedClientRegistrationRequestsRoute} />
      <Route path={"/clients/new"} component={ScopedClientCreateRoute} />
      <Route path={"/clients/:id"} component={ScopedClientDetailRoute} />
      <Route path={"/clientes-tabs"} component={ClientsWithTabs} />
      <Route path={"/configuracoes/usuarios"} component={AdminUsersRoute} />
      <Route path={"/configuracoes/perfis-funcionais"} component={AdminAccessProfilesRoute} />
      <Route path={"/administracao-plataforma/planos-i4h"}>
        {() => <AdminOnlyPage component={I4hPlansAdmin} message="O módulo de Planos I4H fica disponível apenas para ADMIN estrutural dentro de Administração da Plataforma." />}
      </Route>
      <Route
        path={"/usuarios"}
        component={() => {
          if (typeof window !== "undefined") {
            window.location.href = "/configuracoes/usuarios";
          }
          return null;
        }}
      />
      <Route path={"/minha-empresa"} component={MinhaEmpresa} />
      <Route path={"/meus-resultados"} component={MeusResultados} />
      <Route path={"/meus-questionarios"} component={QuestionariosRH} />
      <Route path={"/dashboards/salvia"} component={DashboardsHubSalvia} />
      <Route path={"/dashboards/canal"} component={DashboardsHubCanal} />
      <Route path={"/dashboards/consultor"} component={DashboardsHubConsultor} />
      <Route path={"/dashboards/rh"} component={DashboardsHubRH} />
      <Route path={"/dashboards/ecossistema"} component={SalviaEcossistema} />
      <Route path={"/dashboards/canais"} component={SalviaCanais} />
      <Route path={"/dashboards/gargalos"} component={SalviaGargalos} />
      <Route path={"/dashboards/coleta"} component={SalviaColeta} />
      <Route path={"/dashboards/canal/cockpit"} component={CanalCockpit} />
      <Route path={"/dashboards/canal/jornada"} component={CanalJornada} />
      <Route path={"/dashboards/canal/jornada/:id"} component={CanalJornadaDetalhe} />
      <Route path={"/dashboards/canal/coleta"} component={CanalColeta} />
      <Route path={"/dashboards/canal/pendencias"} component={CanalPendencias} />
      <Route path={"/dashboards/rh/cockpit"} component={RHCockpit} />
      <Route path={"/dashboards/rh/risco"} component={RHRisco} />
      <Route path={"/dashboards/rh/diagnostico"} component={RHDiagnostico} />
      <Route path={"/dashboards/rh/diagnostico/:id"} component={RHDiagnosticoDetalhe} />
      <Route path={"/dashboards/rh/documentos"} component={RHDocumentos} />
      <Route path={"/dashboards/negocios"} component={SalviaNegocios} />
      <Route path={"/dashboards/inteligencia"} component={SalviaMarketIntelligence} />
      <Route path={"/sistema/erros"} component={AdminSystemErrorLogsRoute} />
      <Route path={"/dashboards/consultor/cockpit"} component={ConsultorCockpit} />
      <Route path={"/evaluations"} component={Evaluations} />
      <Route path={"/evaluations/:id/resultado"} component={EvaluationResult} />
      <Route path={"/importacao"}>
        {() => <Nr1StructureAccessPage component={ImportPage} />}
      </Route>
      <Route path={"/questionnaire/:id"} component={Questionnaire} />
      <Route path={"/result/:id"} component={Result} />
      <Route path={"/document/:id"} component={Document} />
      <Route path={"/documents"} component={Documents} />
      <Route path={"/documents/:companyId"} component={DocumentContext} />
      <Route path={"/motor-analise"} component={MotorAnalise} />
      <Route path={"/motor-analise/ergos"} component={ERGOS} />
      <Route path={"/motor-analise/ergos/sugestoes"} component={ERGOSSugestoes} />
      <Route path={"/questionarios"} component={Questionarios} />
      <Route path={"/questionarios/:id"} component={QuestionarioDetalhe} />
      <Route path={"/debug/logs"} component={DebugLogs} />
      <Route path={"/debug/capture-logs"} component={DebugCaptureLogsScript} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <BrandProvider>
            <Toaster />
            <Router />
            <DevTabsBadge />
          </BrandProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
